/**
 * WizardStepIdentity — Step 1: Identity source.
 * Shows pre-loaded identity from bridge OR manual entry.
 */

import React from 'react';
import { Input, TextArea } from '@components/ui';
import { Badge } from '@components/ui';
import type { PersonaIdentity, PersonaIdentityPayload } from '@/types/persona.types';

interface WizardStepIdentityProps {
  identity: PersonaIdentity | null;
  pendingPayload: PersonaIdentityPayload | null;
  onUpdate: (identity: PersonaIdentity) => void;
}

const SOURCE_LABELS: Record<string, string> = {
  crossroads: 'Crossroads Highway',
  'band-persona-architect': 'Band Persona Architect',
  manual: 'Manual Entry',
};

export const WizardStepIdentity: React.FC<WizardStepIdentityProps> = ({ identity, pendingPayload, onUpdate }) => {
  // Initialize from pending payload if identity is null
  const current: PersonaIdentity = identity || {
    source: pendingPayload?.source || 'manual',
    name: pendingPayload?.name || '',
    genre: pendingPayload?.genre || '',
    bandBible: pendingPayload?.bandBible || null,
    markdownIdentity: pendingPayload?.markdown || null,
    styleTags: '',
    excludeTags: '',
  };

  const isFromBridge = current.source !== 'manual';
  const sourceLabel = SOURCE_LABELS[current.source] || current.source;

  const update = (fields: Partial<PersonaIdentity>) => {
    onUpdate({ ...current, ...fields });
  };

  return (
    <div className="space-y-5">
      {isFromBridge && (
        <div className="p-3 bg-amber-950/20 border border-amber-900/30 rounded-xl text-center">
          <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest">Imported from {sourceLabel}</p>
          {current.bandBible && <p className="text-[10px] text-slate-500 mt-1">Full Band Bible data loaded</p>}
          {current.markdownIdentity && <p className="text-[10px] text-slate-500 mt-1">Markdown identity profile loaded ({current.markdownIdentity.length} chars)</p>}
        </div>
      )}

      <div className="space-y-2">
        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Band / Artist Name *</label>
        <Input
          value={current.name}
          onChange={e => update({ name: e.target.value })}
          placeholder="e.g., Crossroads Revival, The Midnight Riders..."
          className="!bg-slate-900 !border-slate-700"
        />
      </div>

      <div className="space-y-2">
        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Primary Genre</label>
        <Input
          value={current.genre}
          onChange={e => update({ genre: e.target.value })}
          placeholder="e.g., Southern Rock, Outlaw Country, Doom Metal..."
          className="!bg-slate-900 !border-slate-700"
        />
      </div>

      <div className="space-y-2">
        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Suno Style Tags</label>
        <Input
          value={current.styleTags}
          onChange={e => update({ styleTags: e.target.value })}
          placeholder="Comma-separated style tags for Suno generation"
          className="!bg-slate-900 !border-slate-700"
        />
      </div>

      <div className="space-y-2">
        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Suno Exclude Tags</label>
        <Input
          value={current.excludeTags}
          onChange={e => update({ excludeTags: e.target.value })}
          placeholder="Comma-separated tags to exclude"
          className="!bg-slate-900 !border-slate-700"
        />
      </div>

      {!isFromBridge && (
        <div className="space-y-2">
          <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Identity Description (Optional)</label>
          <TextArea
            value={current.markdownIdentity || ''}
            onChange={e => update({ markdownIdentity: e.target.value || null, source: 'manual' })}
            placeholder="Describe the band's sound, influences, aesthetic, vocal style..."
            rows={4}
            className="!bg-slate-900 !border-slate-700"
          />
        </div>
      )}

      {/* Preview badges */}
      <div className="flex flex-wrap gap-1.5 pt-2">
        <Badge variant="default-muted">{sourceLabel}</Badge>
        {current.name && <Badge variant="primary">{current.name}</Badge>}
        {current.genre && <Badge variant="info">{current.genre}</Badge>}
        {current.bandBible && <Badge variant="success">Band Bible</Badge>}
        {current.markdownIdentity && <Badge variant="success">Identity Profile</Badge>}
      </div>
    </div>
  );
};
